import {
  Badge,
  cn,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import type { StudentReportLowRemainingItemDTO } from "../api/client";

type StudentReportLowRemainingTableProps = {
  isLoading: boolean;
  data: StudentReportLowRemainingItemDTO[];
  variant?: "table" | "list";
};

function packageTypeLabel(type: StudentReportLowRemainingItemDTO["packageType"]) {
  return type === "package" ? "ແພັກເກດຄລາດສ໌" : "ແພັກເກດຮຽນສ່ວນຕົວ";
}

function remainingLabel(row: StudentReportLowRemainingItemDTO) {
  if (row.packageType === "package") {
    return `${row.remainingSessions ?? 0} / ${row.totalSessions ?? 0} ເວັ້ນໄຊຊັນ`;
  }
  return `${row.remainingHours ?? 0} / ${row.totalHours ?? 0} ຊົ່ວໂມງ`;
}

function LowRemainingEmptyState({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-lg bg-muted/40 px-4 py-10 text-muted-foreground text-sm",
        className,
      )}
    >
      ບໍ່ພົບຂໍ້ມູນ
    </div>
  );
}

function LowRemainingList({
  isLoading,
  data,
}: Pick<StudentReportLowRemainingTableProps, "isLoading" | "data">) {
  if (isLoading) {
    return (
      <ul className="flex flex-col gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <li
            key={`sk-${i}`}
            className="flex items-start justify-between gap-3 rounded-lg border px-3 py-3"
          >
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-56" />
            </div>
            <Skeleton className="h-8 w-24 shrink-0" />
          </li>
        ))}
      </ul>
    );
  }

  if (data.length === 0) {
    return <LowRemainingEmptyState />;
  }

  return (
    <ul className="flex flex-col gap-2">
      {data.map((row, idx) => (
        <li key={`${row.studentId}-${row.packageType}-${idx}`}>
          <Link
            to="/app/students/$id"
            params={{ id: row.studentId }}
            className="flex items-start justify-between gap-3 rounded-lg border bg-card px-3 py-3 transition-colors hover:border-primary/25 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="min-w-0 space-y-1.5">
              <p className="truncate font-medium text-sm leading-snug">
                {row.studentName}
              </p>
              <p className="text-muted-foreground text-xs tabular-nums">
                {row.memberId} · {row.packageName}
              </p>
              <Badge variant="secondary" className="font-normal text-xs">
                {packageTypeLabel(row.packageType)}
              </Badge>
            </div>
            <div className="shrink-0 text-right">
              <p className="font-semibold text-amber-800 text-sm tabular-nums dark:text-amber-300">
                {remainingLabel(row)}
              </p>
              <p className="text-muted-foreground text-xs">ຄົງເຫຼືອ</p>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function LowRemainingTable({
  isLoading,
  data,
}: Pick<StudentReportLowRemainingTableProps, "isLoading" | "data">) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ນັກຮຽນ</TableHead>
            <TableHead>ແພັກເກດ</TableHead>
            <TableHead className="text-right">ຄົງເຫຼືອ</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell
                colSpan={3}
                className="h-24 text-center text-muted-foreground"
              >
                ກຳລັງໂຫລດ...
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={3}
                className="h-24 text-center text-muted-foreground"
              >
                ບໍ່ພົບຂໍ້ມູນ
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, idx) => (
              <TableRow key={`${row.studentId}-${row.packageType}-${idx}`}>
                <TableCell>
                  <Link
                    to="/app/students/$id"
                    params={{ id: row.studentId }}
                    className="font-medium hover:underline"
                  >
                    {row.studentName} ({row.memberId})
                  </Link>
                </TableCell>
                <TableCell>
                  {row.packageName}
                  <span className="ml-1 text-muted-foreground text-xs">
                    {packageTypeLabel(row.packageType)}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {remainingLabel(row)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export function StudentReportLowRemainingTable({
  isLoading,
  data,
  variant = "table",
}: StudentReportLowRemainingTableProps) {
  if (variant === "list") {
    return <LowRemainingList isLoading={isLoading} data={data} />;
  }
  return <LowRemainingTable isLoading={isLoading} data={data} />;
}
